import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, Phone, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function Login() {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'credentials') {
      setStep('otp');
    } else {
      // Handle login
      navigate('/members/dashboard');
    }
  };
  return <div className="min-h-[80vh] flex items-center justify-center bg-club-canvas py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-978">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-979">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-980">
          <defs data-id="element-981">
            <pattern id="pattern-login" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-982">
              <path d="M0 40L40 0H20L0 20M40 40V20L20 40" fill="currentColor" fillOpacity="0.5" data-id="element-983" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#pattern-login)" data-id="element-984" />
        </svg>
      </div>

      <motion.div className="max-w-md w-full space-y-8 bg-white p-10 rounded-sm border border-club-border shadow-sm relative z-10" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} data-id="element-985">
        <div className="text-center" data-id="element-986">
          <div className="mx-auto h-12 w-12 bg-club-canvasAlt rounded-full flex items-center justify-center mb-4" data-id="element-987">
            <Lock className="h-6 w-6 text-club-gold" data-id="element-988" />
          </div>
          <h2 className="text-3xl font-serif text-club-green mb-2" data-id="element-989">
            Member Portal
          </h2>
          <p className="text-sm text-club-ink/60" data-id="element-990">
            Access your exclusive member services and information.
          </p>
        </div>

        <button type="button" onClick={() => navigate('/members/dashboard')} className="w-full btn-gold py-3 flex justify-center items-center gap-2" data-id="element-991">
          Enter Member Portal <ArrowRight size={16} data-id="element-992" />
        </button>

        <div className="relative" data-id="element-993">
          <div className="absolute inset-0 flex items-center" data-id="element-994">
            <div className="w-full border-t border-club-border" data-id="element-995" />
          </div>
          <div className="relative flex justify-center text-xs" data-id="element-996">
            <span className="bg-white px-3 text-club-ink/40 uppercase tracking-wider" data-id="element-997">
              or sign in
            </span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-998">
          {step === 'credentials' ? <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="space-y-6" data-id="element-999">
              {/* Login Method Toggle */}
              <div className="flex p-1 bg-club-canvasAlt rounded-sm" data-id="element-1000">
                <button type="button" onClick={() => setLoginMethod('email')} className={`flex-1 py-2 text-sm font-medium rounded-sm transition-colors ${loginMethod === 'email' ? 'bg-white text-club-green shadow-sm' : 'text-club-ink/60 hover:text-club-ink'}`} data-id="element-1001">
                  Email
                </button>
                <button type="button" onClick={() => setLoginMethod('phone')} className={`flex-1 py-2 text-sm font-medium rounded-sm transition-colors ${loginMethod === 'phone' ? 'bg-white text-club-green shadow-sm' : 'text-club-ink/60 hover:text-club-ink'}`} data-id="element-1002">
                  Mobile Number
                </button>
              </div>

              <div data-id="element-1003">
                <label htmlFor="identifier" className="block text-sm font-medium text-club-ink mb-1" data-id="element-1004">
                  {loginMethod === 'email' ? 'Registered Email Address' : 'Registered Mobile Number'}
                </label>
                <div className="relative" data-id="element-1005">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1006">
                    {loginMethod === 'email' ? <Mail className="h-5 w-5 text-club-ink/40" data-id="element-1007" /> : <Phone className="h-5 w-5 text-club-ink/40" data-id="element-1008" />}
                  </div>
                  <input id="identifier" name="identifier" type={loginMethod === 'email' ? 'email' : 'tel'} className="block w-full pl-10 pr-3 py-3 border border-club-border rounded-sm focus:outline-none focus:ring-1 focus:ring-club-gold focus:border-club-gold sm:text-sm bg-club-canvasAlt/50" placeholder={loginMethod === 'email' ? 'member@example.com' : '+44 7000 000000'} data-id="element-1009" />
                </div>
              </div>

              <button type="submit" className="w-full btn-primary py-3 flex justify-center items-center gap-2" data-id="element-1010">
                Send Access Code <ArrowRight size={16} data-id="element-1011" />
              </button>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="space-y-6" data-id="element-1012">
              <div className="text-center mb-6" data-id="element-1013">
                <ShieldCheck className="h-12 w-12 text-club-green mx-auto mb-4" data-id="element-1014" />
                <p className="text-sm text-club-ink/70" data-id="element-1015">
                  We've sent a 6-digit access code to your {loginMethod}.
                </p>
              </div>

              <div data-id="element-1016">
                <label htmlFor="otp" className="sr-only" data-id="element-1017">
                  Access Code
                </label>
                <input id="otp" name="otp" type="text" maxLength={6} className="block w-full px-3 py-4 text-center text-2xl tracking-[0.5em] font-mono border border-club-border rounded-sm focus:outline-none focus:ring-1 focus:ring-club-gold focus:border-club-gold bg-club-canvasAlt/50" placeholder="------" data-id="element-1018" />
              </div>

              <div className="flex flex-col gap-3" data-id="element-1019">
                <button type="submit" className="w-full btn-primary py-3" data-id="element-1020">
                  Verify & Login
                </button>
                <button type="button" onClick={() => setStep('credentials')} className="w-full text-sm text-club-ink/60 hover:text-club-green transition-colors" data-id="element-1021">
                  Use a different {loginMethod}
                </button>
              </div>
            </motion.div>}
        </form>

        <div className="mt-8 pt-6 border-t border-club-border text-center" data-id="element-1022">
          <p className="text-xs text-club-ink/50 mb-2" data-id="element-1023">
            Having trouble logging in?
          </p>
          <Link to="/contact" className="text-sm text-club-gold hover:text-club-green transition-colors font-medium" data-id="element-1024">
            Contact Member Services
          </Link>
        </div>

        <div className="mt-4 text-center" data-id="element-1025">
          <Link to="/admin/login" className="inline-flex items-center gap-1.5 text-xs text-club-ink/50 hover:text-club-green transition-colors" data-id="element-1026">
            <ShieldCheck size={14} className="text-club-ink/40" data-id="element-1027" />
            Are you an administrator? Go to Staff Login
          </Link>
        </div>
      </motion.div>
    </div>;
}